import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "@/App.css";
import { motion, useInView, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { MapPin, Ticket, Crown, Instagram, Twitter, Facebook, Youtube, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CityComingSoon from "@/pages/CityComingSoon";
import HoustonLivePage from "@/pages/HoustonLivePage";
import DynamicCityPage from "@/pages/DynamicCityPage";
import VIPExperiences from "@/pages/VIPExperiences";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AdminPage from "@/pages/AdminPage";
import Navbar from "@/components/Navbar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cities Context - shares city data across components
const CitiesContext = createContext({ cities: [], loading: true });

export const useCities = () => useContext(CitiesContext);

// Cities Provider - fetches and provides city data
const CitiesProvider = ({ children }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cities`);
        if (res.ok) {
          const data = await res.json();
          setCities(data);
        }
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCities();
  }, []);

  return (
    <CitiesContext.Provider value={{ cities, loading }}>
      {children}
    </CitiesContext.Provider>
  );
};

// Premium transition config - cinematic ease
const sectionTransition = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

// Section Edge Fade - subtle gradient overlay for smooth transitions
const SectionEdgeFade = () => (
  <div 
    className="absolute top-0 left-0 right-0 h-24 pointer-events-none z-[1]"
    style={{ 
      background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, transparent 100%)',
    }}
  />
);

// Fallback city data (used while loading)
const fallbackCities = [
  { cityName: "ATLANTA", slug: "atlanta-ga", status: "coming_soon" },
  { cityName: "AUSTIN", slug: "austin-tx", status: "coming_soon" },
  { cityName: "CHICAGO", slug: "chicago-il", status: "coming_soon" },
  { cityName: "DALLAS", slug: "dallas-tx", status: "coming_soon" },
  { cityName: "HOUSTON", slug: "houston-tx", status: "on_sale" },
  { cityName: "LOS ANGELES", slug: "los-angeles-ca", status: "coming_soon" },
  { cityName: "MIAMI", slug: "miami-fl", status: "coming_soon" },
  { cityName: "NEW YORK", slug: "new-york-ny", status: "coming_soon" },
  { cityName: "SAN DIEGO", slug: "san-diego-ca", status: "coming_soon" },
];

// Legacy cities array for backward compatibility
const cities = [
  { name: "ATLANTA", slug: "atlanta", url: "/city/atlanta-ga" },
  { name: "AUSTIN", slug: "austin", url: "/city/austin-tx" },
  { name: "CHICAGO", slug: "chicago", url: "/city/chicago-il" },
  { name: "DALLAS", slug: "dallas", url: "/city/dallas-tx" },
  { name: "HOUSTON", slug: "houston", url: "/city/houston-tx" },
  { name: "LOS ANGELES", slug: "los-angeles", url: "/city/los-angeles-ca" },
  { name: "MIAMI", slug: "miami", url: "/city/miami-fl" },
  { name: "NEW YORK", slug: "new-york", url: "/city/new-york-ny" },
  { name: "SAN DIEGO", slug: "san-diego", url: "/city/san-diego-ca" },
];

// Decorative Flourish Component
const Flourish = ({ color = "#C24E2C" }) => (
  <div className="flex items-center justify-center gap-3 my-6">
    <div className="w-16 h-0.5" style={{ background: color }} />
    <div className="w-2 h-2 rotate-45" style={{ background: color }} />
    <div className="w-16 h-0.5" style={{ background: color }} />
  </div>
);

// Countdown Timer with Staggered Animation
const CountdownTimer = () => {
  const [time, setTime] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    const target = new Date('2026-05-05T00:00:00');
    const tick = () => {
      const now = new Date();
      const diff = target - now;
      if (diff > 0) {
        setTime({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          mins: Math.floor((diff / 1000 / 60) % 60),
          secs: Math.floor((diff / 1000) % 60),
        });
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, []);

  const boxes = [
    { value: time.days, label: "Days" },
    { value: time.hours, label: "Hours" },
    { value: time.mins, label: "Mins" },
    { value: time.secs, label: "Secs" },
  ];

  return (
    <div ref={ref} className="flex justify-center gap-3 md:gap-4" data-testid="countdown-timer">
      {boxes.map((box, i) => (
        <motion.div
          key={box.label}
          className="countdown-box px-4 py-3 md:px-6 md:py-5 rounded-sm text-center"
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: i * 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="font-heading font-bold text-3xl md:text-5xl text-terracotta">{String(box.value).padStart(2, '0')}</div>
          <div className="text-brown-light text-xs uppercase tracking-widest mt-1 font-medium">{box.label}</div>
        </motion.div>
      ))}
    </div>
  );
};

// Hero Section with Cinematic Video Background and Exit Transition
const HeroSection = ({ onFindCity }) => {
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  // Hero exit transitions - subtle fade as user scrolls past
  const heroOverlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.55, 0.65]);
  const heroContentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.7]);
  const videoParallax = useTransform(scrollYProgress, [0, 1], [0, 150]);
  
  // Custom uploaded video
  const videoUrl = "https://customer-assets.emergentagent.com/job_f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/artifacts/4l57yk97_3ae89577912988570fac901f5747550b_original.mp4";
  
  // Fallback poster image
  const posterUrl = "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/744b8f8483c55b561842543585ff73c11dd754b99c9006a27043cfb41c2ad3ab.png";

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <section 
      ref={heroRef}
      id="home" 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-24 pb-20 relative overflow-hidden" 
      data-testid="hero-section" 
      style={{ backgroundColor: '#050505' }}
    >
      {/* Video Background with Parallax */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: videoParallax }}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ 
            filter: 'grayscale(100%) contrast(1.1) brightness(0.85)',
            transform: 'scale(1.15)',
          }}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={posterUrl}
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      </motion.div>

      {/* Dark overlay for text contrast - fades slightly on scroll */}
      <motion.div 
        className="absolute inset-0 z-[1]"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', opacity: heroOverlayOpacity }}
      />

      {/* Deep green gradient toward bottom */}
      <div 
        className="absolute inset-0 z-[2]"
        style={{ 
          background: 'linear-gradient(180deg, transparent 0%, rgba(10,46,36,0.3) 70%, rgba(15,59,46,0.5) 100%)',
        }}
      />

      {/* Grain Texture Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none z-[3] opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />
      
      <motion.div 
        className="text-center max-w-4xl mx-auto relative z-10"
        style={{ opacity: heroContentOpacity }}
      >
        {/* Gold divider with shimmer */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="relative w-16 h-0.5 overflow-hidden" style={{ background: '#D4AF37' }}>
            <motion.div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 6 }}
            />
          </div>
          <motion.div 
            className="w-2 h-2 rotate-45" 
            style={{ background: '#D4AF37' }}
            animate={{ opacity: [1, 0.6, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="relative w-16 h-0.5 overflow-hidden" style={{ background: '#D4AF37' }}>
            <motion.div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' }}
              animate={{ x: ['200%', '-100%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 6 }}
            />
          </div>
        </div>
        
        {/* Logo Lockup with fade-in rise */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 
            className="font-heading font-black text-6xl md:text-8xl lg:text-9xl uppercase tracking-tight leading-[0.9]"
            style={{ color: '#F4F1E8' }}
          >
            CINCO DE CRAWL
          </h1>
          <p className="font-heading font-bold text-2xl md:text-4xl text-terracotta uppercase tracking-wide mt-3">
            MAY THE 5TH BE WITH YOU
          </p>
        </motion.div>
        
        {/* Terracotta divider with shimmer */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="relative w-16 h-0.5 overflow-hidden" style={{ background: '#C24E2C' }}>
            <motion.div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 7 }}
            />
          </div>
          <div className="w-2 h-2 rotate-45" style={{ background: '#C24E2C' }} />
          <div className="relative w-16 h-0.5 overflow-hidden" style={{ background: '#C24E2C' }}>
            <motion.div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
              animate={{ x: ['200%', '-100%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", repeatDelay: 7 }}
            />
          </div>
        </div>
        
        {/* Supporting descriptor */}
        <motion.p 
          className="text-sm md:text-base tracking-widest mb-12 font-medium uppercase" 
          style={{ color: 'rgba(244,241,232,0.6)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          THE ULTIMATE CINCO DE MAYO BAR CRAWL TOUR
        </motion.p>

        {/* Countdown */}
        <div className="mb-12">
          <p className="text-terracotta uppercase tracking-[0.15em] text-2xl md:text-4xl mb-8 font-heading font-bold">May 5, 2026</p>
          <CountdownTimer />
        </div>

        {/* CTAs with subtle pulse on primary */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <motion.div
            animate={{ 
              boxShadow: [
                '0 0 0 0 rgba(201, 244, 0, 0)',
                '0 0 0 8px rgba(201, 244, 0, 0.15)',
                '0 0 0 0 rgba(201, 244, 0, 0)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 4 }}
            className="rounded-sm"
          >
            <Button onClick={onFindCity} className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-10 py-6 text-sm font-accent font-bold rounded-sm transition-all duration-300 hover:scale-[1.02]" data-testid="find-city-btn">
              <MapPin className="w-4 h-4 mr-2" />
              FIND YOUR CITY
            </Button>
          </motion.div>
          <Button 
            className="bg-transparent border border-white/30 hover:bg-white/5 hover:border-white/50 text-white px-10 py-6 text-sm font-accent font-bold rounded-sm transition-all duration-300" 
            data-testid="vip-access-btn"
            onClick={() => document.getElementById('upgrade')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Crown className="w-4 h-4 mr-2" />
            GET VIP ACCESS
          </Button>
        </div>
      </motion.div>

      <motion.div 
        className="absolute bottom-12 z-10"
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-6 h-6 text-white/40" />
      </motion.div>
    </section>
  );
};

// Premium City Card - Elevated Interaction (API-driven)
const CityCard = ({ city, index }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  
  // Determine the URL and display properties from API data
  const cityUrl = `/city/${city.slug}`;
  const displayName = city.cityName || city.name;

  return (
    <motion.div
      ref={ref}
      className="city-card group block cursor-pointer"
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.08, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      data-testid={`city-tile-${city.slug}`}
      whileTap={{ scale: 0.98 }}
    >
      <a href={cityUrl} className="block h-full">
        <motion.div 
          className="relative h-[100px] md:h-[120px] flex items-center justify-center text-center rounded-[10px]"
          style={{
            background: '#111111',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
          }}
          whileHover={{ 
            y: -6, 
            boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 8px 20px rgba(0,0,0,0.4)',
          }}
          transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span 
            className="relative z-10 font-heading font-black text-xl md:text-2xl lg:text-3xl uppercase tracking-wide transition-colors duration-250 px-4 leading-tight text-[#F4F1E8] group-hover:text-terracotta"
          >
            {displayName}
          </span>
        </motion.div>
      </a>
    </motion.div>
  );
};

// City Section - Matte Black with subtle terracotta warmth (API-driven)
const CitySection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const { cities: apiCities, loading } = useCities();
  
  // Use API cities if available, otherwise fallback
  const displayCities = apiCities.length > 0 ? apiCities : fallbackCities;

  return (
    <section 
      id="cities" 
      className="py-24 md:py-32 px-4 relative overflow-hidden" 
      style={{ background: '#050505' }} 
      ref={ref} 
      data-testid="city-select-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      {/* Subtle terracotta vignette warmth */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(194,78,44,0.02) 0%, transparent 70%)',
        }}
      />
      
      <motion.div 
        className="max-w-5xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="text-center mb-14 md:mb-18"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tight" style={{ color: '#F4F1E8' }}>
            CHOOSE YOUR <span className="text-terracotta">CITY</span>
          </h2>
          {/* Terracotta divider - animate from center outward */}
          <motion.div 
            className="relative h-0.5 mx-auto mt-8" 
            style={{ background: '#C24E2C', width: 64 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-7 max-w-4xl mx-auto">
          {displayCities
            .sort((a, b) => (a.cityName || a.name || '').localeCompare(b.cityName || b.name || ''))
            .map((city, i) => <CityCard key={city.slug || city.id} city={city} index={i} />)}
        </div>
      </motion.div>
    </section>
  );
};

// Icons - Terracotta and Marigold only
const IconVinyl = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="20" stroke="#C24E2C" strokeWidth="3"/>
    <circle cx="24" cy="24" r="8" stroke="#E6A817" strokeWidth="3"/>
    <circle cx="24" cy="24" r="3" fill="#E6A817"/>
  </svg>
);

const IconShotGlass = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <path d="M14 8H34L30 40H18L14 8Z" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8H36" stroke="#E6A817" strokeWidth="3" strokeLinecap="round"/>
    <path d="M20 40H28" stroke="#E6A817" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const IconWristband = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <rect x="4" y="16" width="40" height="16" rx="8" stroke="#C24E2C" strokeWidth="3"/>
    <path d="M12 20V28" stroke="#E6A817" strokeWidth="2" strokeLinecap="round"/>
    <path d="M18 20V28" stroke="#E6A817" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="36" cy="24" r="4" fill="#E6A817"/>
  </svg>
);

const IconAgave = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <path d="M24 44V28" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M24 28C24 28 16 24 12 14C12 14 18 18 24 18C30 18 36 14 36 14C32 24 24 28 24 28Z" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 18V4" stroke="#E6A817" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="24" cy="8" r="3" fill="#E6A817"/>
  </svg>
);

const IconFlash = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <rect x="8" y="8" width="32" height="32" rx="4" stroke="#C24E2C" strokeWidth="3"/>
    <circle cx="24" cy="24" r="8" stroke="#E6A817" strokeWidth="3"/>
    <circle cx="24" cy="24" r="3" fill="#E6A817"/>
  </svg>
);

const IconVenues = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <circle cx="12" cy="20" r="6" stroke="#C24E2C" strokeWidth="3"/>
    <circle cx="36" cy="20" r="6" stroke="#C24E2C" strokeWidth="3"/>
    <circle cx="24" cy="36" r="6" stroke="#E6A817" strokeWidth="3"/>
    <path d="M17 22L19 34" stroke="#E6A817" strokeWidth="2" strokeLinecap="round"/>
    <path d="M31 22L29 34" stroke="#E6A817" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IconCrew = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="14" r="6" stroke="#C24E2C" strokeWidth="3"/>
    <circle cx="10" cy="20" r="5" stroke="#E6A817" strokeWidth="3"/>
    <circle cx="38" cy="20" r="5" stroke="#E6A817" strokeWidth="3"/>
    <path d="M16 38V32C16 28 20 26 24 26C28 26 32 28 32 32V38" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

const IconMerch = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <path d="M12 8L24 4L36 8" stroke="#E6A817" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8V40H36V8" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="24" cy="24" r="6" stroke="#E6A817" strokeWidth="3"/>
  </svg>
);

const IconSurprise = () => (
  <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
    <path d="M24 4V12" stroke="#E6A817" strokeWidth="3" strokeLinecap="round"/>
    <path d="M24 36V44" stroke="#E6A817" strokeWidth="3" strokeLinecap="round"/>
    <path d="M4 24H12" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M36 24H44" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M10 10L16 16" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M32 32L38 38" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M10 38L16 32" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <path d="M32 16L38 10" stroke="#C24E2C" strokeWidth="3" strokeLinecap="round"/>
    <circle cx="24" cy="24" r="6" fill="#E6A817"/>
  </svg>
);

// Experience Section - Deep Emerald Green with Vignette
const ExperienceSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  const features = [
    { icon: IconVinyl, title: "NON-STOP BEATS", desc: "All night heat." },
    { icon: IconShotGlass, title: "$3 BEERS. $5 SHOTS.", desc: "Every. Bar." },
    { icon: IconWristband, title: "ONE WRISTBAND. ALL ACCESS.", desc: "No limits." },
    { icon: IconAgave, title: "PREMIUM TEQUILA", desc: "VIP only." },
    { icon: IconFlash, title: "CAPTURE IT.", desc: "Make it iconic." },
    { icon: IconVenues, title: "OWN THE CITY.", desc: "Multiple venues." },
    { icon: IconCrew, title: "SQUAD UP.", desc: "Big group energy." },
    { icon: IconMerch, title: "REP THE CRAWL.", desc: "Official merch." },
    { icon: IconSurprise, title: "STAY READY.", desc: "VIP surprises." },
  ];

  return (
    <section 
      id="experience"
      className="py-28 md:py-40 px-4 relative overflow-hidden" 
      ref={ref} 
      data-testid="experience-section"
      style={{ 
        background: 'linear-gradient(180deg, #0F3B2E 0%, #0A2E24 50%, #0F3B2E 100%)',
      }}
    >
      {/* Section edge fade - smooth transition from hero */}
      <SectionEdgeFade />
      
      {/* Vignette overlay for intensity */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, transparent 0%, rgba(5,20,15,0.4) 100%)',
        }}
      />
      
      <motion.div 
        className="max-w-6xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="text-center mb-16 md:mb-24"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight mb-6">
            <span style={{ color: '#F4F1E8' }}>THE </span>
            <span className="text-terracotta">EXPERIENCE</span>
          </h2>
          {/* Gold divider - animate from center outward, triggers before text */}
          <motion.div 
            className="relative h-0.5 mx-auto" 
            style={{ background: '#D4AF37', width: 80 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-14 md:gap-y-20">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              className="text-center group cursor-default"
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: 1.04 }}
              data-testid={`feature-${i}`}
            >
              <div className="flex justify-center mb-5 transition-all duration-250 group-hover:drop-shadow-[0_0_16px_rgba(194,78,44,0.5)]">
                <f.icon />
              </div>
              <h3 className="font-accent font-bold text-sm md:text-base mb-2 tracking-wider uppercase" style={{ color: '#F4F1E8' }}>{f.title}</h3>
              <p className="font-light text-sm" style={{ color: 'rgba(244,241,232,0.5)' }}>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

// Bold Statement Section - "We Don't Crawl Quiet."
const StatementSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section 
      ref={ref}
      className="py-20 md:py-28 px-4 relative"
      style={{ background: '#0B0B0F' }}
      data-testid="statement-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      <motion.div 
        className="text-center"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="font-heading font-black text-4xl md:text-6xl lg:text-7xl uppercase tracking-tight" style={{ color: '#F4F1E8' }}>
          We Don't <span className="text-terracotta">Crawl</span> Quiet.
        </h2>
      </motion.div>
    </section>
  );
};

// VIP Section - Black with subtle terracotta warmth
const VIPSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section 
      id="upgrade" 
      className="py-32 md:py-44 px-4 relative overflow-hidden" 
      style={{ background: '#0B0B0F' }}
      ref={ref} 
      data-testid="vip-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      {/* Subtle terracotta vignette warmth */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 100% 80% at 50% 100%, rgba(194,78,44,0.04) 0%, transparent 60%)',
        }}
      />
      
      <motion.div 
        className="max-w-4xl mx-auto text-center relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Terracotta divider - animate from center */}
          <motion.div 
            className="relative h-0.5 bg-terracotta mx-auto mb-10"
            style={{ width: 64 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
          
          <h2 className="font-heading font-black text-6xl md:text-8xl lg:text-9xl uppercase mb-8 tracking-tight leading-[0.85]">
            <span className="text-white block">GO <span className="text-terracotta">VIP.</span></span>
            <span className="text-white block">GO <span className="text-marigold">HARDER.</span></span>
          </h2>
          
          <p className="text-white/50 text-lg md:text-xl max-w-lg mx-auto mb-12 font-light">
            Skip the lines. Own the night.
          </p>

          <a href="/vip">
            <Button className="bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] px-14 py-7 text-base font-accent font-bold rounded-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(201,244,0,0.25)]" data-testid="upgrade-vip-btn">
              UPGRADE YOUR NIGHT
            </Button>
          </a>
          
          {/* Terracotta divider - animate from center */}
          <motion.div 
            className="relative h-0.5 bg-terracotta mx-auto mt-12"
            style={{ width: 64 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Email Section - Black with subtle terracotta warmth
const EmailSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          source: 'homepage'
        })
      });
      
      if (response.ok) {
        setDone(true);
      } else {
        const data = await response.json();
        if (data.detail === "Already signed up") {
          setError("You're already signed up!");
          setTimeout(() => setDone(true), 1500);
        } else {
          setError("Something went wrong. Please try again.");
        }
      }
    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section 
      id="specials"
      className="py-24 md:py-32 px-4 relative overflow-hidden" 
      style={{ background: '#0B0B0F' }}
      ref={ref} 
      data-testid="email-capture-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      {/* Subtle terracotta vignette warmth */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(194,78,44,0.03) 0%, transparent 50%)',
        }}
      />
      
      <motion.div 
        className="max-w-lg mx-auto text-center relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl text-white uppercase mb-4 tracking-tight leading-[0.9]">
            <span className="text-terracotta">UNLOCK</span> EXCLUSIVE<br />DRINK SPECIALS
          </h2>
          
          {/* Terracotta divider - animate from center */}
          <motion.div 
            className="relative h-0.5 bg-terracotta mx-auto my-6"
            style={{ width: 48 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
          
          {/* Micro-tagline */}
          <p className="text-white/30 text-sm italic mb-2">
            Get the drops before the drop.
          </p>
          
          <p className="text-white/40 text-base mb-8 font-light">
            Sign up before grabbing tickets.
          </p>

          <AnimatePresence mode="wait">
            {!done ? (
              <motion.form onSubmit={submit} className="flex flex-col gap-4" key="form" exit={{ opacity: 0, y: -10 }}>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-terracotta transition-colors duration-300"
                  required
                  data-testid="name-input"
                />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-terracotta transition-colors duration-300"
                  required
                  data-testid="email-input"
                />
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-[#1A1A1A] border-white/10 text-white placeholder:text-white/30 py-5 px-5 text-base rounded-sm focus:border-terracotta transition-colors duration-300"
                  required
                  data-testid="phone-input"
                />
                {error && (
                  <p className="text-terracotta text-sm" data-testid="email-error">{error}</p>
                )}
                <Button type="submit" disabled={loading} className="w-full bg-[#C9F400] hover:bg-[#D8FF40] text-[#0B0B0F] py-6 font-accent font-bold text-sm rounded-sm mt-2 transition-all duration-300 hover:shadow-[0_4px_20px_rgba(201,244,0,0.2)] disabled:opacity-50" data-testid="email-submit-btn">
                  {loading ? "UNLOCKING..." : "UNLOCK DEALS"}
                </Button>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-center gap-4 py-6"
                data-testid="email-success"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C24E2C] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <div className="text-center">
                  <p className="font-heading font-black text-2xl uppercase tracking-tight mb-1" style={{ color: "#F4F1E8" }}>
                    YOU'RE <span className="text-terracotta">IN!</span>
                  </p>
                  <p className="text-white/40 text-sm">Check your inbox for exclusive deals.</p>
                </div>
                <div className="w-12 h-0.5 mt-2" style={{ background: "#D4AF37" }} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </section>
  );
};

// THE CRAWL CODE Section - Emerald Green
const CrawlCodeSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  const codeItems = [
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="#D4AF37" strokeWidth="1.5">
          <rect x="4" y="8" width="24" height="18" rx="2" />
          <path d="M4 14h24" />
          <circle cx="16" cy="21" r="2" />
        </svg>
      ),
      text: "Respect the venues."
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="#D4AF37" strokeWidth="1.5">
          <path d="M16 4v4M16 24v4M8 16H4M28 16h-4" />
          <circle cx="16" cy="16" r="6" />
          <path d="M13 14l2 4 4-2" />
        </svg>
      ),
      text: "Hydrate between stops."
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="#D4AF37" strokeWidth="1.5">
          <circle cx="10" cy="22" r="4" />
          <circle cx="22" cy="22" r="4" />
          <path d="M14 22h4M10 18V10l12-4v12" />
        </svg>
      ),
      text: "Move smart. Ride safe."
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="#D4AF37" strokeWidth="1.5">
          <path d="M16 6l2 4 4.5.5-3.25 3.25.75 4.5L16 16.5l-4 1.75.75-4.5L9.5 10.5 14 10l2-4z" />
          <circle cx="16" cy="22" r="4" />
        </svg>
      ),
      text: "Bring good energy only."
    }
  ];

  return (
    <section 
      ref={ref}
      id="the-code"
      className="py-24 md:py-32 px-4 relative overflow-hidden" 
      style={{ background: 'linear-gradient(180deg, #0F3B2E 0%, #0A2E24 50%, #0F3B2E 100%)' }}
      data-testid="crawl-code-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      {/* Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 50%, transparent 0%, rgba(5,20,15,0.4) 100%)',
        }}
      />
      
      <motion.div 
        className="max-w-4xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-tight" style={{ color: '#F4F1E8' }}>
            THE CRAWL <span className="text-terracotta">CODE</span>
          </h2>
          
          {/* Gold divider with shimmer */}
          <div className="relative w-16 h-0.5 mx-auto mt-6 overflow-hidden" style={{ background: '#D4AF37' }}>
            <motion.div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 5 }}
            />
          </div>
          
          <p className="text-white/40 text-sm tracking-widest uppercase mt-4">
            How we move on Cinco.
          </p>
        </motion.div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12 max-w-2xl mx-auto">
          {codeItems.map((item, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex justify-center mb-4">
                {item.icon}
              </div>
              <p className="font-accent font-semibold text-base tracking-wide" style={{ color: '#F4F1E8' }}>
                {item.text}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom tagline */}
        <motion.p
          className="text-center mt-16 text-white/25 text-xs tracking-widest uppercase"
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          We're here to celebrate — not complicate.
        </motion.p>
      </motion.div>
    </section>
  );
};

// Official Partners Section - Premium Full-Color Display
const PartnersSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  const partnersRow1 = [
    { name: "Patrón", logo: "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/38db5d871a1927be9f8661befb9a93a57dd27834170c83a8152fad69e2f8c401.png", glow: "rgba(212,175,55,0.15)", textFallback: null },
    { name: "Teremana", logo: "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/492a61c129cd302d01b5fbd13bd63f90c858fca54a19fde5c2e350cb9540be41.png", glow: "rgba(194,156,96,0.12)", textFallback: null },
    { name: "Lyft", logo: null, glow: "rgba(234,0,137,0.15)", textFallback: { text: "LYFT", color: "#EA0089" } },
    { name: "Liquid Death", logo: "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/04d6698bb98a81be190f89ad045917caf40ae388e2829c9ff6b636a0e133aa3f.png", glow: "rgba(200,200,200,0.1)", textFallback: null }
  ];

  const partnersRow2 = [
    { name: "Marriott", logo: "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/4f37cd0717eb47de99524bbcc2ca015b2d5192e4d30c6ee880047a4e9aa816c8.png", glow: "rgba(212,175,55,0.12)", textFallback: null },
    { name: "Hilton", logo: null, glow: "rgba(0,106,166,0.15)", textFallback: { text: "HILTON", color: "#006AA6" } },
    { name: "Cash App", logo: "https://static.prod-images.emergentagent.com/jobs/f6bea0b8-83b2-4aab-b0e3-44e3cfafd1d1/images/482ff762e63908294d33563948aff6e671c27700df76046f108e80552b90ffc4.png", glow: "rgba(0,214,120,0.15)", textFallback: null }
  ];

  const LogoItem = ({ partner, index }) => (
    <motion.div
      className="group relative flex items-center justify-center"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: 0.15 + index * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Subtle radial glow behind logo */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 80% at 50% 50%, ${partner.glow} 0%, transparent 70%)`,
          transform: 'scale(1.5)',
        }}
      />
      <motion.div 
        className="relative px-6 md:px-10 lg:px-14 py-6"
        whileHover={{ 
          scale: 1.04, 
          y: -4,
        }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {partner.logo ? (
          <img 
            src={partner.logo} 
            alt={partner.name}
            className="h-16 md:h-20 lg:h-24 w-auto object-contain transition-all duration-300 group-hover:drop-shadow-[0_8px_24px_rgba(0,0,0,0.4)]"
          />
        ) : (
          <span 
            className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight transition-all duration-300 group-hover:drop-shadow-[0_4px_16px_rgba(0,0,0,0.3)]"
            style={{ color: partner.textFallback?.color || '#F4F1E8' }}
          >
            {partner.textFallback?.text}
          </span>
        )}
      </motion.div>
    </motion.div>
  );

  // Desktop marquee - all partners in slow horizontal scroll
  const allPartners = [...partnersRow1, ...partnersRow2];

  return (
    <section 
      ref={ref}
      className="py-24 md:py-32 px-4 relative overflow-hidden" 
      style={{ background: '#0B0B0B' }}
      data-testid="partners-section"
    >
      {/* Section edge fade */}
      <SectionEdgeFade />
      
      {/* Subtle ambient glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 50% 40%, rgba(212,175,55,0.02) 0%, transparent 60%)',
        }}
      />
      
      <motion.div 
        className="max-w-6xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Heading */}
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight" style={{ color: '#F4F1E8' }}>
            OFFICIAL PARTNERS
          </h2>
          {/* Gold divider */}
          <motion.div 
            className="relative h-0.5 mx-auto mt-6"
            style={{ background: '#D4AF37', width: 50 }}
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.05, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
          {/* Subtext */}
          <p className="text-white/30 text-sm md:text-base tracking-wide mt-5 font-light">
            Brands that move culture.
          </p>
        </motion.div>

        {/* Desktop: Static 2-Row Grid with subtle slow marquee */}
        <div className="hidden md:block">
          {/* Marquee container */}
          <div className="relative overflow-hidden">
            <motion.div 
              className="flex items-center justify-center gap-4 lg:gap-6"
              animate={{ x: [0, -50, 0] }}
              transition={{ 
                duration: 20, 
                repeat: Infinity, 
                ease: "easeInOut",
              }}
            >
              {/* Row 1 */}
              <div className="flex justify-center items-center">
                {partnersRow1.map((partner, i) => (
                  <LogoItem key={partner.name} partner={partner} index={i} />
                ))}
              </div>
            </motion.div>
          </div>
          
          {/* Row 2 - offset animation */}
          <div className="mt-8 relative overflow-hidden">
            <motion.div 
              className="flex items-center justify-center gap-4 lg:gap-6"
              animate={{ x: [0, 40, 0] }}
              transition={{ 
                duration: 18, 
                repeat: Infinity, 
                ease: "easeInOut",
              }}
            >
              <div className="flex justify-center items-center">
                {partnersRow2.map((partner, i) => (
                  <LogoItem key={partner.name} partner={partner} index={i + 4} />
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile: Stacked Grid (no marquee) */}
        <div className="md:hidden">
          <div className="grid grid-cols-2 gap-6">
            {[...partnersRow1, ...partnersRow2].map((partner, i) => (
              <motion.div
                key={partner.name}
                className="flex items-center justify-center relative"
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse 100% 100% at 50% 50%, ${partner.glow} 0%, transparent 70%)`,
                  }}
                />
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="h-12 w-auto object-contain relative z-10"
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <motion.p
          className="text-center mt-16 md:mt-20 text-white/20 text-xs tracking-wide"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          Interested in partnering with the tour?{' '}
          <a 
            href="/contact" 
            className="text-white/30 hover:text-terracotta transition-colors duration-200 underline underline-offset-2"
          >
            Contact us
          </a>
        </motion.p>
      </motion.div>
    </section>
  );
};

// Footer - Black
const Footer = () => {
  const socials = [
    { icon: Instagram, label: "Instagram" },
    { icon: Twitter, label: "Twitter" },
    { icon: Facebook, label: "Facebook" },
    { icon: Youtube, label: "Youtube" },
  ];

  return (
    <footer className="py-16 px-4 bg-[#0B0B0F] border-t border-white/5" data-testid="footer">
      <div className="max-w-6xl mx-auto text-center">
        <h3 className="font-heading font-bold text-2xl text-white mb-2 tracking-tight">
          CINCO <span className="text-terracotta">DE</span> CRAWL
        </h3>
        <p className="text-white/30 text-sm mb-8">The Ultimate Bar Crawl Tour</p>

        <div className="flex justify-center gap-4 mb-8">
          {socials.map((s) => (
            <a key={s.label} href="#" className="w-10 h-10 flex items-center justify-center text-white/40 hover:text-[#C9F400] transition-colors border border-white/10 rounded-full hover:border-[#C9F400]/30" aria-label={s.label} data-testid={`social-${s.label.toLowerCase()}`}>
              <s.icon className="w-5 h-5" />
            </a>
          ))}
        </div>

        <div className="w-12 h-0.5 bg-forest mx-auto mb-8" />

        <div className="flex flex-wrap justify-center gap-6 text-sm mb-8">
          <a href="/privacy" className="text-white/30 hover:text-white transition-colors" data-testid="privacy-link">Privacy</a>
          <a href="/terms" className="text-white/30 hover:text-white transition-colors" data-testid="terms-link">Terms</a>
          <a href="/contact" className="text-white/30 hover:text-white transition-colors" data-testid="contact-link">Contact</a>
        </div>

        <p className="text-white/20 text-xs">© 2026 Cinco De Crawl. Must be 21+.</p>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-cream">
      <main>
        {/* HERO - Cream (LOCKED) */}
        <HeroSection onFindCity={() => scrollTo('cities')} />
        
        {/* EXPERIENCE - Deep Emerald Green */}
        <ExperienceSection />
        
        {/* CITY SELECTION - Matte Black */}
        <CitySection />
        
        {/* VIP - Near Black */}
        <VIPSection />
        
        {/* THE CRAWL CODE - Emerald Green */}
        <CrawlCodeSection />
        
        {/* EMAIL CAPTURE - Near Black */}
        <EmailSection />
      </main>
      <Footer />
    </div>
  );
};

// Layout component for pages with navbar
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
  </>
);

// Main App with Router
function App() {
  return (
    <CitiesProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Admin page - no navbar */}
            <Route path="/admin456" element={<AdminPage />} />
            {/* Public pages with navbar */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/vip" element={<PublicLayout><VIPExperiences /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
            {/* Dynamic city routing - loads correct template based on status */}
            <Route path="/city/:slug" element={<PublicLayout><DynamicCityPage /></PublicLayout>} />
          </Routes>
        </div>
      </Router>
    </CitiesProvider>
  );
}

export default App;
